import React, { useCallback, useMemo, useState } from 'react'
import useParams from 'core/hooks/useParams'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { updateVolumeType } from './actions'
import ModalForm from 'core/elements/modal/ModalForm'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import VolumeBackendPicklist from './VolumeBackendPicklist'
import PicklistField from 'core/components/validatedForm/DropdownField'

const useStyles = makeStyles<Theme>((theme) => ({
  sameLine: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
  },
}))

interface Props {
  rows: any[]
  onClose: () => void
}

export default function EditVolumeTypeModal({ rows: [volumeType], onClose }: Props) {
  const classes = useStyles()
  const [errorMessage, setErrorMessage] = useState<{ title: string; message: string } | undefined>(
    undefined,
  )

  const defaultParams = useMemo(
    () => ({
      volumeBackend:
        (Object.entries(volumeType.extra_specs).find(
          ([key]) => key === 'volume_backend_name',
        )?.[1] as string) || '',
      metadata: Object.entries(volumeType.extra_specs)
        .filter(([key]) => key !== 'volume_backend_name')
        .map(([key, value]) => {
          return { key, value }
        }),
      public: volumeType.is_public,
    }),
    [volumeType.extra_specs],
  )

  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  const { update, updating, error, reset } = useUpdateAction(updateVolumeType)

  const submitForm = useCallback(async () => {
    if (params.metadata.some((data) => data.key === 'volume_backend_name')) {
      setErrorMessage({
        title: 'Invalid Key-Value Pair',
        message:
          'Cannot add volume_backend_name in Metadata Key-Value pair. Please select the Volume Backend from the dropdown',
      })
      return
    }
    setErrorMessage(undefined)

    // calc new/update keys
    const originalMetadata = volumeType?.extra_specs

    // Calc delete keys
    const originalMetadataKeys = Object.keys(originalMetadata)

    const inititalValue = !!params.volumeBackend
      ? { volume_backend_name: params.volumeBackend }
      : {}

    const newMetadataObj = params.metadata.reduce((accum, pair) => {
      return {
        ...accum,
        [pair.key]: pair.value,
      }
    }, inititalValue)
    const deleteKeys = originalMetadataKeys.filter((key) => {
      return !newMetadataObj[key]
    })

    const body = {
      extra_specs: newMetadataObj,
      is_public: params.public,
    }
    const { success } = await update({ id: volumeType.id, body, keysToDelete: deleteKeys })
    if (!success) return
    handleClose()
  }, [volumeType?.id, volumeType?.extra_specs, params])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    onClose()
  }

  return (
    <ModalForm
      title={`Edit Volume Type`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating}
      error={error || errorMessage}
      submitTitle={`Update Volume Type`}
      open
    >
      <>
        <FormFieldSection title="Volume Type Metadata">
          <PicklistField
            DropdownComponent={VolumeBackendPicklist}
            id="volumeType"
            onChange={getParamsUpdater('volumeBackend')}
            value={params.volumeBackend}
            label="Volume Backend"
            includeNoneOption
          />
        </FormFieldSection>
        <FormFieldSection title="Volume Type Metadata">
          <KeyValuesField
            id="metadata"
            label="Metadata"
            value={params.metadata}
            onChange={getParamsUpdater('metadata')}
            addLabel="Add Metadata"
            allowMultipleValues
          />
        </FormFieldSection>
        <FormFieldSection title="Tenant Access">
          <CheckboxField
            id="public"
            label="Make Public"
            value={params.public}
            onChange={getParamsUpdater('public')}
            info="If checked, volume type will be available for every tenant."
          />
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
